import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ProjectDto, UpdateProjectInput } from '@cutlyft/zod-schemas';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';

interface UpdateProjectApiResponse {
  success: boolean;
  data: ProjectDto;
}

interface UpdateProjectMutationInput {
  id: number;
  input: UpdateProjectInput;
}

/**
 * Mutation hook for updating an existing project's metadata.
 * Calls PATCH /projects/:id.
 * Invalidates both the project detail and the list on success.
 */
export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: UpdateProjectMutationInput) => {
      const response = await apiClient.patch<UpdateProjectApiResponse>(`/projects/${id}`, input);
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.byId(data.id) });
    },
  });
}
